import { OverlayRenderer } from "./OverlayRenderer";
import {
  OverlayType,
  DEFAULT_OVERLAY_STYLE,
  type AnyOverlay,
  type OverlayStyle,
  type PolylineOverlay,
  type LineOverlay,
  type PointOverlay,
  type CircleOverlay,
  type ArcOverlay,
  type RectangleOverlay,
  type TextOverlay,
  type EllipseOverlay,
} from "./types";

/**
 * OverlayManager - Manages overlay graphics for preview and feedback.
 *
 * Creates and manages a 2D canvas overlay positioned on top of the main
 * WebGL canvas. Provides methods for adding, updating, and clearing
 * overlay graphics without affecting the main scene render.
 */
export class OverlayManager {
  private overlayCanvas: HTMLCanvasElement;
  private renderer: OverlayRenderer;
  private overlays = new Map<string, AnyOverlay>();
  private nextId = 0;
  /** Nesting depth of batch() calls: redraws are deferred while > 0. */
  private batchDepth = 0;
  /** A redraw was requested during the current batch. */
  private redrawPending = false;

  constructor(mainCanvas: HTMLCanvasElement) {
    // Create overlay canvas
    this.overlayCanvas = this.createOverlayCanvas(mainCanvas);
    this.renderer = new OverlayRenderer(this.overlayCanvas);

    // Initial resize
    this.resize();

    // Watch for main canvas resize
    this.setupResizeObserver(mainCanvas);
  }

  // ============== Canvas Setup ==============

  private createOverlayCanvas(
    mainCanvas: HTMLCanvasElement,
  ): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.id = "overlay-canvas";
    canvas.style.position = "absolute";
    canvas.style.top = "0";
    canvas.style.left = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.pointerEvents = "none"; // Let events pass through
    // z-index: above canvas but below toolbars (100) and menus (1000)
    // Must match Z_INDEX_OVERLAY from layouts/constants.ts
    canvas.style.zIndex = "10";

    // Insert after main canvas
    mainCanvas.parentElement?.appendChild(canvas);

    return canvas;
  }

  private setupResizeObserver(mainCanvas: HTMLCanvasElement): void {
    const observer = new ResizeObserver(() => {
      this.resize();
    });
    observer.observe(mainCanvas);
  }

  /**
   * Resize the overlay canvas to match the main canvas.
   */
  resize(): void {
    const parent = this.overlayCanvas.parentElement;
    if (parent) {
      const width = parent.clientWidth;
      const height = parent.clientHeight;
      this.renderer.resize(width, height);
      this.redraw();
    }
  }

  // ============== Overlay Management ==============

  /**
   * Add an overlay and return its ID.
   */
  add(overlay: Omit<AnyOverlay, "id">): string {
    const id = `overlay-${this.nextId++}`;
    const fullOverlay = { ...overlay, id } as AnyOverlay;
    this.overlays.set(id, fullOverlay);
    this.redraw();
    return id;
  }

  /**
   * Update an existing overlay.
   */
  update(id: string, updates: Partial<AnyOverlay>): void {
    const existing = this.overlays.get(id);
    if (existing) {
      this.overlays.set(id, { ...existing, ...updates } as AnyOverlay);
      this.redraw();
    }
  }

  /**
   * Remove an overlay by ID.
   */
  remove(id: string): void {
    if (this.overlays.delete(id)) {
      this.redraw();
    }
  }

  /**
   * Clear all overlays.
   */
  clear(): void {
    this.overlays.clear();
    this.renderer.clear();
  }

  /**
   * Get an overlay by ID.
   */
  get(id: string): AnyOverlay | undefined {
    return this.overlays.get(id);
  }

  /**
   * Redraw all overlays (deferred to the end of the enclosing batch, if any).
   */
  redraw(): void {
    if (this.batchDepth > 0) {
      this.redrawPending = true;
      return;
    }
    this.renderer.clear();
    this.renderer.renderAll(Array.from(this.overlays.values()));
  }

  /**
   * Run several add/update/remove calls with a single redraw at the end.
   * Each of them otherwise repaints every overlay: rebuilding N handles one
   * by one costs O(N²) draws.
   */
  batch(mutate: () => void): void {
    this.batchDepth++;
    try {
      mutate();
    } finally {
      this.batchDepth--;
      if (this.batchDepth === 0 && this.redrawPending) {
        this.redrawPending = false;
        this.redraw();
      }
    }
  }

  // ============== Convenience Methods ==============

  /**
   * Create a polyline preview (e.g., for freehand drawing).
   */
  createPolyline(
    points: Array<{ x: number; y: number }>,
    style?: Partial<OverlayStyle>,
  ): string {
    return this.add({
      type: OverlayType.Polyline,
      points,
      closed: false,
      style: { ...DEFAULT_OVERLAY_STYLE, ...style },
    } as Omit<PolylineOverlay, "id">);
  }

  /**
   * Update polyline points.
   */
  updatePolyline(id: string, points: Array<{ x: number; y: number }>): void {
    this.update(id, { points } as Partial<PolylineOverlay>);
  }

  /**
   * Add a point to an existing polyline.
   */
  addPointToPolyline(id: string, point: { x: number; y: number }): void {
    const overlay = this.overlays.get(id) as PolylineOverlay | undefined;
    if (overlay && overlay.type === OverlayType.Polyline) {
      overlay.points.push(point);
      this.redraw();
    }
  }

  /**
   * Create a line preview.
   */
  createLine(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    style?: Partial<OverlayStyle>,
  ): string {
    return this.add({
      type: OverlayType.Line,
      x1,
      y1,
      x2,
      y2,
      style: { ...DEFAULT_OVERLAY_STYLE, ...style },
    } as Omit<LineOverlay, "id">);
  }

  /**
   * Create a point marker.
   */
  createPoint(
    x: number,
    y: number,
    radius?: number,
    style?: Partial<OverlayStyle>,
  ): string {
    return this.add({
      type: OverlayType.Point,
      x,
      y,
      radius,
      style: { ...DEFAULT_OVERLAY_STYLE, ...style },
    } as Omit<PointOverlay, "id">);
  }

  /**
   * Create a circle preview.
   */
  createCircle(
    cx: number,
    cy: number,
    radius: number,
    style?: Partial<OverlayStyle>,
  ): string {
    return this.add({
      type: OverlayType.Circle,
      cx,
      cy,
      radius,
      style: { ...DEFAULT_OVERLAY_STYLE, ...style },
    } as Omit<CircleOverlay, "id">);
  }

  /**
   * Create an arc preview.
   */
  createArc(
    cx: number,
    cy: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    style?: Partial<OverlayStyle>,
    counterClockwise?: boolean,
  ): string {
    return this.add({
      type: OverlayType.Arc,
      cx,
      cy,
      radius,
      startAngle,
      endAngle,
      counterClockwise,
      style: { ...DEFAULT_OVERLAY_STYLE, ...style },
    } as Omit<ArcOverlay, "id">);
  }

  /**
   * Create a rectangle preview.
   */
  createRectangle(
    x: number,
    y: number,
    width: number,
    height: number,
    style?: Partial<OverlayStyle>,
  ): string {
    return this.add({
      type: OverlayType.Rectangle,
      x,
      y,
      width,
      height,
      style: { ...DEFAULT_OVERLAY_STYLE, ...style },
    } as Omit<RectangleOverlay, "id">);
  }

  /**
   * Create a text label.
   */
  createText(
    x: number,
    y: number,
    text: string,
    style?: Partial<OverlayStyle>,
  ): string {
    return this.add({
      type: OverlayType.Text,
      x,
      y,
      text,
      style: { ...DEFAULT_OVERLAY_STYLE, fillColor: "#000000", ...style },
    } as Omit<TextOverlay, "id">);
  }

  /**
   * Create an ellipse
   */
  createEllipse(
    cx: number,
    cy: number,
    radiusx: number,
    radiusy: number,
    rotation: number,
    style?: Partial<OverlayStyle>,
  ) {
    return this.add({
      type: OverlayType.Ellipse,
      cx,
      cy,
      radiusx,
      radiusy,
      rotation,
      style: { ...DEFAULT_OVERLAY_STYLE, ...style },
    } as EllipseOverlay);
  }

  // ============== Lifecycle ==============

  /**
   * Dispose the overlay manager.
   */
  dispose(): void {
    this.clear();
    this.overlayCanvas.remove();
  }
}
